import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import OvLabelledField from '../atoms/OvLabelledField';
import OvPillLink from '../atoms/OvPillLink';
import OvStackedDateTime from '../atoms/OvStackedDateTime';
import OvVideoEditOrderButton from '../molecules/OvVideoEditOrderButton';
import OvVideoShownIn from '../molecules/OvVideoShownIn';
import OvVideoStatusSummary from '../molecules/OvVideoStatusSummary';
import OvVideoSummary from '../molecules/OvVideoSummary';
import { AdminVideo } from '../../../common/model/dto/video/admin-video';
import { VideoPlacement } from '../../../common/model/dto/video/video-placement';
import { VideoUploadItem } from '../../../context/video-upload.types';
import { isOrderEditable } from '../../../common/utils/video-placement-utils';
import useRowNavigation from '../../../hooks/use-row-navigation';
import breakpoints from '../../../design-system/breakpoints';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvVideoListCardProps {
  video: AdminVideo;
  detailsPath: string;
  placements: VideoPlacement[] | undefined;
  upload?: VideoUploadItem;
  isUnseen?: boolean;
  onEditOrder?: (video: AdminVideo) => void;
}

const OvVideoListCard: FC<OvVideoListCardProps> = ({
  video,
  detailsPath,
  placements,
  upload,
  isUnseen = false,
  onEditOrder,
}) => {
  const { t } = useTranslation();
  const navigate = useRowNavigation(detailsPath);

  return (
    <Card data-muted={isUnseen || undefined} onClick={navigate}>
      <Top>
        <Heading>
          <OvVideoSummary
            video={video}
            detailsPath={detailsPath}
            isUnseen={isUnseen}
          />
          <OvVideoStatusSummary video={video} upload={upload} />
        </Heading>
        <DetailsLink
          to={detailsPath}
          aria-label={t('videos.list.detailsFor', { title: video.title })}
        >
          {t('videos.list.details')}
        </DetailsLink>
      </Top>
      <Fields>
        <OvLabelledField label={t('videos.fields.shownIn')}>
          <OvVideoShownIn video={video} placements={placements} upload={upload}>
            {onEditOrder && isOrderEditable(video) && (
              <OvVideoEditOrderButton video={video} onEdit={onEditOrder} />
            )}
          </OvVideoShownIn>
        </OvLabelledField>
        <OvLabelledField label={t('videos.fields.createdAt')}>
          <OvStackedDateTime value={video.created_at} />
        </OvLabelledField>
      </Fields>
    </Card>
  );
};

export default OvVideoListCard;

const Card = styled.li`
  padding: ${Variables.spacing.MEDIUM} ${Variables.spacing.SMALL};
  border-bottom: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  list-style: none;
  cursor: pointer;

  &:hover {
    background-color: ${Colours.OV_WHITE_HOVER};
  }

  &[data-muted] {
    color: ${Colours.OV_CHARCOAL};
  }

  &:last-child {
    border-bottom: 0;
  }
`;

const Top = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${Variables.spacing.LARGE};
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Variables.spacing.SMALL};
  min-width: 0;
`;

const DetailsLink = styled(OvPillLink)`
  flex: none;

  @media (max-width: ${breakpoints.sm}) {
    display: none;
  }
`;

const Fields = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(8.5rem, 1fr));
  gap: ${Variables.spacing.SMALL} ${Variables.spacing.LARGE};
  margin: ${Variables.spacing.SMALL} 0 0;
`;
